/**
 * One mailbox, everything the module knows about it.
 *
 * This is the panel the shared-address report exists for: the plan's argument
 * is that an address behind forty companies is worth ONE personal letter rather
 * than forty automatic ones, and writing that letter means knowing who the
 * forty are. So the company list is the first thing on it, with a link into the
 * register for each.
 *
 * ⚠ THREE THINGS IT DELIBERATELY DOES NOT CLAIM.
 *
 *   The register list is what the register HOLDS, and only the first e-mail
 *   channel of each company ever reaches that column - the rest live in a JSON
 *   field nothing here reads. So this is "companies whose filed address is
 *   this", not "every company reachable at it".
 *
 *   The letter history reaches back only as far as retention allows. A swept
 *   ledger row has its key rewritten to `purged:<id>`, which unlinks it from
 *   the address for good; there is no way to count what is missing, so the
 *   panel says the limit rather than inventing a number.
 *
 *   An address with no ledger row is a normal answer, not a failure. Most of
 *   the shared report has never been enrolled - it was screened out before a
 *   ledger row existed.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Ban, Building2, ExternalLink, Loader2, Mail } from "lucide-react";
import { useT } from "../../../../i18n/I18nProvider";
import { userCanPlatform, type StaffUser } from "../../../../lib/auth";
import { getAddressDetail, type AddressDetail } from "../../../../lib/marketingContacts";
import { Modal, StatusPill, fmtNum, fmtStamp } from "../../../ui";
import { Note, num } from "../shared";
import { contactTone, recipientTone } from "../status";
import { keyDiffers } from "./lib";

export function AddressModal({
  address,
  user,
  onClose,
}: {
  address: string;
  user: StaffUser;
  onClose: () => void;
}) {
  const t = useT();
  const canOpenRegister = userCanPlatform(user, "view:ariregister");

  const [detail, setDetail] = useState<AddressDetail | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let alive = true;
    setDetail(null);
    setError(false);
    getAddressDetail(address)
      .then((d) => {
        if (alive) setDetail(d);
      })
      .catch(() => {
        if (alive) setError(true);
      });
    return () => {
      alive = false;
    };
  }, [address]);

  return (
    <Modal title={address} label={t("marketing.contacts.detail.title")} onClose={onClose} className="max-w-[520px]">
      <div className="space-y-4 px-[26px] py-5">
        {error ? <Note tone="bad">{t("marketing.error.load")}</Note> : null}

        {!detail && !error ? (
          <div className="flex justify-center py-10 text-muted-foreground">
            <Loader2 className="size-6 animate-spin" />
          </div>
        ) : null}

        {detail ? (
          <>
            {/* The ledger files gmail dots and plus-tags away. Two strings that
                look like two mailboxes and are one; without this line an
                operator comparing them concludes the history is somebody
                else's. */}
            {keyDiffers(detail.address, detail.emailKey) ? (
              <Note tone="info">
                {t("marketing.contacts.detail.filedAs")}{" "}
                <b className="crm-tnum">{detail.emailKey}</b>
              </Note>
            ) : null}

            {detail.suppression ? (
              <Note tone="warn" icon={<Ban className="size-3.5" />}>
                {t("marketing.contacts.detail.suppressed", {
                  source: t(`marketing.suppression.source.${detail.suppression.source}`),
                  at: fmtStamp(detail.suppression.createdAt),
                })}
                {detail.suppression.reason ? ` - ${detail.suppression.reason}` : ""}
              </Note>
            ) : null}

            {/* ── The mailbox's own record ───────────────────────────────── */}
            <section>
              <h3 className="crm-lab !mt-0">{t("marketing.contacts.detail.ledger")}</h3>
              {detail.contact ? (
                <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-2 text-[12.75px]">
                  <Fact label={t("marketing.contacts.ledger.col.standing")}>
                    <StatusPill tone={contactTone(detail.contact.status)}>
                      {t(`marketing.contacts.status.${detail.contact.status}`)}
                    </StatusPill>
                  </Fact>
                  <Fact label={t("marketing.contacts.detail.personType")}>
                    {t(`marketing.contacts.person.${detail.contact.personType}`)}
                    <span className="crm-mut">
                      {" "}
                      · {t(`marketing.contacts.source.${detail.contact.personSource}`)}
                    </span>
                  </Fact>
                  <Fact label={t("marketing.contacts.ledger.col.letters")}>
                    <span className="crm-tnum">{num(detail.contact.contactCount)}</span>
                  </Fact>
                  <Fact label={t("marketing.contacts.ledger.col.last")}>
                    <span className="crm-tnum">
                      {detail.contact.lastContactedAt ? fmtStamp(detail.contact.lastContactedAt) : "-"}
                    </span>
                  </Fact>
                </dl>
              ) : (
                <p className="crm-sub">{t("marketing.contacts.detail.notInLedger")}</p>
              )}
            </section>

            {/* ── Who is behind it, in the register ──────────────────────── */}
            <section>
              <h3 className="crm-lab">
                {t("marketing.contacts.detail.companies", { total: fmtNum(detail.companyTotal) })}
              </h3>
              {detail.companies.length === 0 ? (
                <p className="crm-sub">{t("marketing.contacts.detail.noCompanies")}</p>
              ) : (
                <ul className="mt-2 max-h-64 space-y-1.5 overflow-y-auto pr-1">
                  {detail.companies.map((c) => (
                    <li key={c.regCode} className="flex items-center gap-2 text-[12.75px]">
                      <Building2 className="size-3.5 shrink-0 text-muted-foreground" />
                      {canOpenRegister ? (
                        <Link to={`/ariregister/${c.regCode}`} className="crm-trunc hover:text-primary">
                          {c.name}
                        </Link>
                      ) : (
                        <span className="crm-trunc">{c.name}</span>
                      )}
                      {c.status && c.status !== "R" ? (
                        <span className="crm-mut shrink-0">{t(`ariregister.status.${c.status}`)}</span>
                      ) : null}
                      {c.webUrl ? <ExternalLink className="size-3 shrink-0 text-muted-foreground" /> : null}
                    </li>
                  ))}
                </ul>
              )}
              {detail.companyTotal > detail.companies.length ? (
                <p className="crm-sub">
                  {t("marketing.contacts.detail.companiesCap", {
                    shown: fmtNum(detail.companies.length),
                    total: fmtNum(detail.companyTotal),
                  })}
                </p>
              ) : null}
              <p className="crm-sub">{t("marketing.contacts.detail.firstChannel")}</p>
            </section>

            {/* ── What we have written, and on what footing ──────────────── */}
            <section>
              <h3 className="crm-lab">
                {t("marketing.contacts.detail.letters", { total: fmtNum(detail.letterTotal) })}
              </h3>
              {detail.letters.length === 0 ? (
                <p className="crm-sub">{t("marketing.contacts.detail.noLetters")}</p>
              ) : (
                <ul className="mt-2 max-h-72 space-y-2 overflow-y-auto pr-1">
                  {detail.letters.map((l) => (
                    <li key={l.id} className="flex items-start gap-2.5 text-[12.75px]">
                      <Mail className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" />
                      <span className="min-w-0 flex-1">
                        <span className="flex items-center gap-2">
                          <Link to={`/marketing/campaigns/${l.campaignId}`} className="crm-trunc hover:text-primary">
                            {l.campaignName ?? t("marketing.contacts.detail.deletedCampaign")}
                          </Link>
                          <StatusPill tone={recipientTone(l.status)}>
                            {t(`marketing.recipientStatus.${l.status}`)}
                          </StatusPill>
                        </span>
                        <span className="crm-mut crm-tnum block">
                          {/* `sentAt` is when a letter left; `eventAt` is when
                              something came back, days later. A row with
                              neither was never sent, so it says when it was
                              written down instead. */}
                          {fmtStamp(l.sentAt ?? l.eventAt ?? l.queuedAt)}
                          {l.skipReason ? ` · ${t(`marketing.skip.${l.skipReason}`)}` : ""}
                          {l.legalState ? ` · ${t(`marketing.contacts.legal.${l.legalState}`)}` : ""}
                        </span>
                        {/* Shown for retrying rows too: `sendOne` writes the
                            transport's message and leaves the status pending
                            while another attempt is scheduled. */}
                        {l.error ? (
                          <span className="crm-mut flex items-start gap-1.5 text-[11.5px]">
                            <AlertTriangle className="mt-0.5 size-3 shrink-0" />
                            <span className="min-w-0">{l.error}</span>
                          </span>
                        ) : null}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
              <p className="crm-sub">{t("marketing.contacts.detail.historyNote")}</p>
            </section>
          </>
        ) : null}
      </div>
    </Modal>
  );
}

/** One labelled fact. Local because it is four lines and belongs to this
 *  panel's two-column grid, not to the module's vocabulary. */
function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="crm-mut text-[11.5px]">{label}</dt>
      <dd className="mt-0.5">{children}</dd>
    </div>
  );
}
